import { defaultMapCoordinates } from "@/App/constants/constants";
import { Body, Bold, Paragraph } from "capitalrx-components-2.0/lib/typography";
import GoogleMapReact from "google-map-react";
import { FC, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import MapMarker from "./MapMarker.png";
import { fitBounds } from "google-map-react";

export interface MapLocation {
  readonly longitude: string;
  readonly latitude: string;
  readonly displayTitle?: string;
  readonly displayDescription?: string;
}

interface MapProps {
  readonly center?: {
    readonly longitude: string;
    readonly latitude: string;
  };
  readonly locations: MapLocation[];
  readonly mapHeight?: string;
}

interface MarkerProps {
  readonly lat: number;
  readonly lng: number;
  readonly displayTitle?: string;
  readonly displayDescription?: string;
  readonly showingDisplay?: boolean;
  readonly onClick?: () => void;
}

const MarkerIcon = styled.div`
  background-image: url(${MapMarker});
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  width: 30px;
  height: 30px;
  position: absolute;
  top: -15px;
  left: -15px;
`;

const MarkerPopup = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 6px;
  position: absolute;
  left: 12px;
  top: -15px;
  z-index: 808080;
  min-width: 200px;
`;

const MapWrapper = styled.div<{ readonly mapheight: string }>`
  min-height: ${(props) => props.mapheight};
`;

const Marker: FC<MarkerProps> = (props) => {
  const { displayTitle, displayDescription, showingDisplay } = props;

  return (
    <>
      {showingDisplay && displayTitle && displayDescription && (
        <MarkerPopup>
          <Body size="mini">
            <Bold>{displayTitle}</Bold>
            <Paragraph>{displayDescription}</Paragraph>
          </Body>
        </MarkerPopup>
      )}
      <MarkerIcon onClick={() => props.onClick && props.onClick()} />
    </>
  );
};

export const Map: FC<MapProps> = ({ locations, center, mapHeight = "400px" }) => {
  const [displayingMarkerIndex, setDisplayingMarkerIndex] = useState(undefined);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState(15);

  const onMarkerPressed = useCallback(
    (markerIndex: number) => {
      if (displayingMarkerIndex === markerIndex) {
        setDisplayingMarkerIndex(undefined);
      } else {
        setDisplayingMarkerIndex(markerIndex);
        const marker = locations[markerIndex];
        setMapCenter({
          lat: Number(marker.latitude),
          lng: Number(marker.longitude),
        });
      }
    },
    [displayingMarkerIndex],
  );

  useEffect(() => {
    if (center) {
      setMapCenter({
        lat: Number(center.latitude) || defaultMapCoordinates.lat,
        lng: Number(center.longitude) || defaultMapCoordinates.lng,
      });
      setMapZoom(15);
    } else if (locations.length === 1) {
      setMapCenter({
        lat: Number(locations[0].latitude),
        lng: Number(locations[0].longitude),
      });
      setMapZoom(15);
    } else {
      const northernMost = Math.max(...locations.map((l) => Number(l.latitude)));
      const easternMost = Math.max(...locations.map((l) => Number(l.longitude)));
      const southernMost = Math.min(...locations.map((l) => Number(l.latitude)));
      const westernMost = Math.min(...locations.map((l) => Number(l.longitude)));

      const { center: newCenter, zoom: newZoom } = fitBounds(
        {
          ne: {
            lat: northernMost,
            lng: easternMost,
          },
          sw: {
            lat: southernMost,
            lng: westernMost,
          },
        },
        {
          width: 400,
          height: 400,
        },
      );
      setMapCenter(newCenter);
      setMapZoom(newZoom);
    }
  }, [locations]);

  return (
    <MapWrapper mapheight={mapHeight}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_TEMP_GOOGLE_MAP_API_KEY }}
        center={mapCenter}
        defaultZoom={mapZoom}
        zoom={mapZoom}
        yesIWantToUseGoogleMapApiInternals
        shouldUnregisterMapOnUnmount
        onClick={() => setDisplayingMarkerIndex(undefined)}
      >
        {locations.map((location, idx) => {
          return (
            <Marker
              onClick={() => onMarkerPressed(idx)}
              showingDisplay={idx === displayingMarkerIndex}
              displayDescription={location.displayDescription}
              displayTitle={location.displayTitle}
              key={idx.toString()}
              lat={Number(location.latitude)}
              lng={Number(location.longitude)}
            />
          );
        })}
      </GoogleMapReact>
    </MapWrapper>
  );
};
